import { useState, useEffect } from 'react';
import { AccountCircle } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { Grid, Typography, TextField } from '@mui/material';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import SimpleCard from '../../../../../components/SimpleCard';
import { setVendorRetailPayload } from '../../../../../core/vendor-retail/action';
import WizardButtons from '../WizardButtons';

const validationSchema = Yup.object({
  contact_name: Yup.string().required('Name is required'),
  contact_phone: Yup.string()
    .matches(/^[0-9]{10}$/, 'Phone number must be 10 digits')
    .required('Phone number is required'),
  contact_email: Yup.string().email('Invalid email').required('Email is required'),
});

export default function PointOfContact({ nextStep, initialValues, ...rest }) {
  const { vendorRetailPayload } = useSelector((store) => store.vendorRetail);
  const { user } = useSelector((store) => store.users);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  // Auto-fill initial values with user data if not already set
  const getInitialValues = () => {
    // If already saved in payload, use that
    if (vendorRetailPayload?.contactName) {
      return {
        contact_name: vendorRetailPayload.contactName,
        contact_phone: vendorRetailPayload.contactPhone || '',
        contact_email: vendorRetailPayload.contactEmail || '',
      };
    }

    // Otherwise, auto-fill with user data
    if (user) {
      const userName =
        user.name && user.last_name ? `${user.name} ${user.last_name}` : user.name || '';

      // Strip +1 country code from phone number if present
      let phoneNumber = user.phone_number || '';
      if (phoneNumber.startsWith('+1')) {
        phoneNumber = phoneNumber.substring(2); // Remove "+1"
      } else if (phoneNumber.startsWith('1') && phoneNumber.length === 11) {
        phoneNumber = phoneNumber.substring(1); // Remove leading "1"
      }

      return {
        contact_name: userName,
        contact_phone: phoneNumber,
        contact_email: user.email || '',
      };
    }

    // Fallback to empty values
    return initialValues;
  };

  const formik = useFormik({
    initialValues: getInitialValues(),
    validationSchema,
    enableReinitialize: true, // Allow form to reinitialize when user data loads
    onSubmit: async (values) => {
      setLoading(true);

      // Update payload with contact info
      dispatch(
        setVendorRetailPayload({
          contactName: values.contact_name,
          contactPhone: values.contact_phone,
          contactEmail: values.contact_email,
        }),
      );

      // Move to next step after a short delay
      setTimeout(() => {
        nextStep();
        setLoading(false);
      }, 300);
    },
  });

  return (
    <div>
      <form onSubmit={formik.handleSubmit}>
        <Grid container justifyContent="center" mb={2}>
          <Grid item>
            <Typography fontWeight="bold">Point of Contact:</Typography>
          </Grid>
          <Grid item>
            <Typography marginLeft={2}>Who will be at this retail space?</Typography>
          </Grid>
        </Grid>
        <SimpleCard variant="outlined" sx={{ display: 'flex' }}>
          <Grid container columns={10} justifyContent={{ xs: 'center', md: 'left' }}>
            <Grid item xs={2} textAlign="center">
              <AccountCircle style={{ fontSize: '5rem', color: '#EE7B57' }} />
            </Grid>
            <Grid item xs={8}>
              <TextField
                style={{ marginBottom: '1rem' }}
                fullWidth
                label="Name"
                name="contact_name"
                value={formik.values.contact_name || ''}
                onChange={formik.handleChange}
                variant="standard"
                size="small"
                error={formik.touched.contact_name && Boolean(formik.errors.contact_name)}
                helperText={formik.touched.contact_name && formik.errors.contact_name}
              />
              <TextField
                style={{ marginBottom: '1rem' }}
                fullWidth
                label="Phone Number"
                name="contact_phone"
                inputProps={{ maxLength: 10 }}
                value={formik.values.contact_phone || ''}
                onChange={formik.handleChange}
                variant="standard"
                size="small"
                error={formik.touched.contact_phone && Boolean(formik.errors.contact_phone)}
                helperText={formik.touched.contact_phone && formik.errors.contact_phone}
              />
              <TextField
                style={{ marginBottom: '1rem' }}
                fullWidth
                label="Email"
                name="contact_email"
                type="email"
                value={formik.values.contact_email || ''}
                onChange={formik.handleChange}
                variant="standard"
                size="small"
                error={formik.touched.contact_email && Boolean(formik.errors.contact_email)}
                helperText={formik.touched.contact_email && formik.errors.contact_email}
              />
            </Grid>
          </Grid>
        </SimpleCard>
        <WizardButtons
          nextStep={nextStep}
          nextLabelButtonType="submit"
          loading={loading}
          {...rest}
        />
      </form>
    </div>
  );
}

PointOfContact.propTypes = {
  nextStep: PropTypes.func,
  initialValues: PropTypes.instanceOf(Object),
};

PointOfContact.defaultProps = {
  nextStep: () => {},
  initialValues: {
    contact_name: '',
    contact_email: '',
    contact_phone: '',
  },
};
